import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/AccordionSideMenu';
import ChevronLeftIcon from '@/components/Icons/ChevronLeft';
import PromotionFillIcon from '@/components/Icons/PromotionFill';
import { useSideMenu } from '@/components/NewHeader/SideMenu/useSideMenu';
import { useTranslation } from '@/contexts/dictionaryContext';
import { cn } from '@/lib/utils';
import Link from 'next/link';

export function BackButton() {
  const { setCurrentContent } = useSideMenu();
  const {
    newHeader: { sideMenu },
  } = useTranslation();

  return (
    <button
      className="flex items-center px-2 pt-2 pb-4"
      onClick={() => setCurrentContent('initial')}>
      <ChevronLeftIcon className="size-5" /> {sideMenu.back}
    </button>
  );
}

export function Item({
  value,
  isOpen,
  children,
}: {
  value: string;
  isOpen: boolean;
  children: React.ReactNode;
}) {
  return (
    <AccordionItem
      value={value}
      className={cn(
        'border-l-white/0 border-l-4 px-0 transition-[border-color]',
        isOpen && 'border-l-primary',
      )}>
      <AccordionTrigger className="uppercase font-bold px-4 text-gray-900">
        {value}
      </AccordionTrigger>
      {children}
    </AccordionItem>
  );
}

export function BigContent({
  items,
  onItemClick,
}: {
  items: { title: string; items: { title: string; href: string }[] }[];
  onItemClick: (item: { title: string; href: string }) => void;
}) {
  const { close } = useSideMenu();

  const handleClick = (item: { title: string; href: string }) => {
    onItemClick(item);
    close();
  };

  return (
    <AccordionContent className="space-y-5 px-4 border-b border-black/5 text-gray-800">
      {items.map((list) => (
        <div key={list.title} className="space-y-3">
          <p className="font-bold text-gray-900">{list.title}</p>
          {list.items.map((item) => (
            <Link
              href={item.href}
              onClick={() => handleClick(item)}
              key={item.title}
              className="block">
              {item.title}
            </Link>
          ))}
        </div>
      ))}
    </AccordionContent>
  );
}

export function PromotionalSideOffers({
  title,
  items,
  onItemClick,
}: {
  title: string;
  items: { title: string; href: string }[];
  onItemClick: (item: { title: string; href: string }) => void;
}) {
  const { close } = useSideMenu();

  if (!items.length) {
    return null;
  }

  const handleClick = (item: { title: string; href: string }) => {
    onItemClick(item);
    close();
  };

  return (
    <div className="border-b border-primary/20 bg-primary/5 px-4 py-4 space-y-3 text-primary">
      <p className="flex items-center gap-2 font-bold uppercase">
        <PromotionFillIcon className="size-4 shrink-0 text-primary" />
        {title}
      </p>
      <div className="space-y-3 max-h-40 overflow-y-auto">
        {items.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            onClick={() => handleClick(item)}
            className="flex items-center gap-2 font-medium">
            <PromotionFillIcon className="size-4 shrink-0 text-primary" />
            {item.title}
          </Link>
        ))}
      </div>
    </div>
  );
}

export function DataList({
  data,
  scrollable = false,
  onItemClick,
}: {
  data: {
    title: string;
    items: { title: string; href: string }[];
  };
  scrollable?: boolean;
  onItemClick: (item: { title: string; href: string }) => void;
}) {
  const { close } = useSideMenu();

  const handleClick = (item: { title: string; href: string }) => {
    onItemClick(item);
    close();
  };

  return (
    <div className={cn('space-y-3', scrollable && 'max-h-40 overflow-y-auto')}>
      {data.items.map((item) => (
        <Link
          key={item.href}
          href={item.href}
          onClick={() => handleClick(item)}
          className="flex items-center gap-2 font-medium">
          <PromotionFillIcon className="size-4 shrink-0 text-primary" />
          {item.title}
        </Link>
      ))}
    </div>
  );
}

export function SmallContent({
  items,
  children,
  onItemClick,
}: {
  items: { title: string; href: string }[];
  onItemClick: (item: { title: string; href: string }) => void;
  children?: React.ReactNode;
}) {
  const { close } = useSideMenu();

  const handleClick = (item: { title: string; href: string }) => {
    onItemClick(item);
    close();
  };

  return (
    <AccordionContent className="space-y-3 px-4 border-b border-black/5 text-gray-800">
      {items.map((list) => (
        <div key={list.title}>
          <Link
            href={list.href}
            onClick={() => handleClick(list)}
            className="block">
            {list.title}
          </Link>
        </div>
      ))}
      {children}
    </AccordionContent>
  );
}
